import React, { useState, ReactNode } from 'react'
import {
  Button,
  Grid,
  Theme,
  useMediaQuery,
  Typography,
  Link as MuiLink,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { KeyboardArrowDown } from '@material-ui/icons'
import clsx from 'clsx'
import {
  Order,
  Quote,
  SavedCart,
  SharedList,
  OrderStatusType,
} from '@src/types/graphql-types'
import {
  homeRoute,
  loginRoute,
  useRouter,
  aldrichMarketSelectRoute,
} from '@src/routes'
import { useCurrentUser } from '@utils/useCurrentUser'
import messages from '@utils/messages'
import { useIntl, FormattedMessage, FormatDateOptions } from 'react-intl'
import { QuickOrderModalContentType } from '@src/components/QuickOrder/utils'
import AddToOrderModal from './AddToOrderModal/AddToOrderModal'
import RecentOrders from './RecentOrders'
import Quotes from './Quotes'
import SavedCarts from './SavedCarts'
import Lists from './Lists'
import LoadingBox from '@src/components/LoadingBox'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  DejaNewEventCategory as eventCategory,
  DejaNewEventAction as eventAction,
} from '@utils/analytics/enums'
import { useCookies } from '@src/utils/cookies'
import { Link } from '@src/components/Link'
import ContentRow from './ContentRow'
import { useGetSharedListsQuery } from '@src/queries/SharedListQueries.generated'
import { isPast } from 'date-fns-next'
import CommonCard from '@src/components/Common/CommonCard'

const useStyles = makeStyles((theme: Theme) => ({
  active: {},
  dropdownButtonWrapper: {
    alignItems: 'center',
    display: 'inline-flex',
    fontSize: theme.typography.pxToRem(14),
    justifyContent: 'center',
    lineHeight: 1,
    margin: theme.spacing(2, 0),
  },
  marketSelect: {
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    textAlign: 'initial',
    lineHeight: 1,
  },
  header: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(18),
    margin: 0,
  },
  addToOrderLoginContainer: {
    paddingBottom: theme.spacing(2),
  },
  addToOrderLoginDescription: {
    paddingBottom: theme.spacing(4),
  },
  addToOrderLoginLink: {
    marginBottom: theme.spacing(4),
  },
  icon: {
    '&$active': {
      transform: 'rotate(180deg)',
    },
  },
  link: {
    justifyContent: 'left',
    padding: 0,
  },
  contentAreaChildren: {
    marginBottom: theme.spacing(1),
  },
  skeletonRow: {
    margin: theme.spacing(1, 0),
  },
}))

const LoadingSkeleton = () => {
  const classes = useStyles()

  return (
    <>
      <Grid item xs={12} className={classes.skeletonRow}>
        <LoadingBox height={24} width={0.7} />
      </Grid>
      <Grid item xs={12} className={classes.skeletonRow}>
        <LoadingBox height={24} width={0.4} />
      </Grid>
      <Grid item xs={12} className={classes.skeletonRow}>
        <LoadingBox height={24} width={0.5} />
      </Grid>
    </>
  )
}

interface ContentAreaProps {
  children: ReactNode
  gaEvent: () => void
  label: ReactNode
}

const ContentArea = ({ children, gaEvent, label }: ContentAreaProps) => {
  const classes = useStyles()
  const [active, setActive] = useState(false)

  const handleClick = () => {
    gaEvent()
    setActive(!active)
  }

  return (
    <>
      <Grid item xs={12}>
        <Button onClick={handleClick} className={classes.link}>
          <span className={classes.dropdownButtonWrapper}>
            <span>{label}</span>
            <KeyboardArrowDown
              className={clsx(classes.icon, { [classes.active]: active })}
            />
          </span>
        </Button>
      </Grid>
      {active && (
        <>
          <Grid item xs={1} />
          <Grid
            item
            container
            spacing={2}
            xs={11}
            className={classes.contentAreaChildren}
          >
            {children}
          </Grid>
        </>
      )}
    </>
  )
}

interface AddToOrderProps {
  contentTypes: QuickOrderModalContentType[]
}

const LISTS_LIMIT = 5

const AddToOrder: React.FC<AddToOrderProps> = ({ contentTypes }) => {
  const classes = useStyles()
  const theme = useTheme()
  const router = useRouter()
  const { userIsLoggedIn, userId, isB2BUser } = useCurrentUser()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContentType, setModalContentType] =
    useState<QuickOrderModalContentType>(QuickOrderModalContentType.Order)
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null)
  const [orderAccordionOpen, setOrderAccordionOpen] = useState(false)
  const [currentQuote, setCurrentQuote] = useState<Quote | null>(null)
  const [quoteAccordionOpen, setQuoteAccordionOpen] = useState(false)
  const [currentSavedCart, setCurrentSavedCart] = useState<SavedCart | null>(
    null
  )
  const [savedCartAccordionOpen, setSavedCartAccordionOpen] = useState(false)
  const [currentList, setCurrentList] = useState<SharedList | null>(null)
  const [listAccordionOpen, setListAccordionOpen] = useState(false)
  const { formatDate } = useIntl()

  const [cookies] = useCookies(['_ga'])

  const getDate = (value, useUTC = false) => {
    let options: FormatDateOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
    if (useUTC) {
      options = { ...options, timeZone: 'UTC' }
    }
    return formatDate(new Date(value), options)
  }

  const handleOrderClick = (order: any) => {
    const date = getDate(order.orderDate)
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'open recent order modal',
        detail: `${date} - ${order.salesDocNumber}`,
        section: 'add to quick order',
        component: 'right rail',
        elementType: 'link',
        elementText: `${date} ${order.salesDocNumber}`,
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.RecentOrders,
        eventLabel: `${date} > ${order.salesDocNumber}`,
        eventInteractionType: 0,
      }
    )
    setCurrentOrder(order)
    setModalContentType(QuickOrderModalContentType.Order)
    setIsModalOpen(true)
  }

  const handleSavedCartClick = (savedCart: any) => {
    const date = getDate(savedCart.savedDate)
    setCurrentSavedCart(savedCart)
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'open saved cart modal',
        detail: `${date}`,
        section: 'add to quick order',
        component: 'right rail',
        elementType: 'link',
        elementText: `${savedCart.cartName} ${date}`,
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: 'saved cart',
        eventLabel: userId,
        eventInteractionType: 0,
      }
    )
    setModalContentType(QuickOrderModalContentType.SavedCart)
    setIsModalOpen(true)
  }

  const handleQuoteClick = (quote: any) => {
    const expired =
      quote.validTo > 0 &&
      isPast(quote.validTo) &&
      quote.status !== OrderStatusType.Complete
    const date = getDate(quote.validTo, true)
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'open quote modal',
        detail: `${expired ? 'exp. ' : ''}${date} - ${quote.salesDocNumber}`,
        section: 'add to quick order',
        component: 'right rail',
        elementType: 'link',
        elementText: `${expired ? 'exp. ' : ''}${date} ${quote.salesDocNumber}`,
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.Quotes,
        eventLabel: `${date} > ${quote.salesDocNumber}`,
        eventInteractionType: 0,
      }
    )
    setCurrentQuote(quote)
    setModalContentType(QuickOrderModalContentType.Quote)
    setIsModalOpen(true)
  }

  const handleListClick = (list: any) => {
    const date = getDate(list.lastUpdatedDate)
    setCurrentList(list)
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'open list modal',
        detail: `${date}`,
        section: 'add to quick order',
        component: 'right rail',
        elementType: 'link',
        elementText: `${list.name} ${date}`,
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.Lists,
        eventLabel: userId,
        eventInteractionType: 0,
      }
    )
    setModalContentType(QuickOrderModalContentType.List)
    setIsModalOpen(true)
  }

  const getTrackingPayload = (isOpen: boolean, actionItem: string) => ({
    event: 'quick_order_widget_interaction',
    action: `${isOpen ? 'collapse' : 'expand'} ${actionItem}`,
    section: 'add to quick order',
    component: 'right rail',
    elementType: 'accordion',
    elementText: actionItem,
    coreEvent: 'no',
  })

  const { data: listsData, loading: listsLoading } = useGetSharedListsQuery({
    ssr: false,
    skip: !userIsLoggedIn,
    variables: {
      input: {
        perpage: LISTS_LIMIT,
        sortcolumn: 'lastUpdatedDate',
      },
    },
  })
  const lists = listsData?.getSharedLists.sharedLists
  const noListsItems =
    !listsData?.getSharedLists.sharedLists ||
    listsData?.getSharedLists.sharedLists.length === 0

  const renderType = (type: QuickOrderModalContentType, i) => {
    // eslint-disable-next-line default-case
    switch (type) {
      case QuickOrderModalContentType.Order:
        return (
          <ContentArea
            key={`content-area-${i}`}
            gaEvent={() => {
              sendCommonDetailClickEvent(
                getTrackingPayload(
                  orderAccordionOpen,
                  eventAction.RecentOrders
                ),
                {
                  eventCategory: eventCategory.QuickOrderPage,
                  eventAction: eventAction.RecentOrders,
                  eventLabel: userId,
                  eventInteractionType: 0,
                }
              )
              setOrderAccordionOpen(!orderAccordionOpen)
            }}
            label={<FormattedMessage {...messages.RECENT_ORDERS} />}
          >
            <RecentOrders handleOrderClick={handleOrderClick} />
          </ContentArea>
        )
      case QuickOrderModalContentType.Quote:
        return (
          <ContentArea
            key={`content-area-${i}`}
            gaEvent={() => {
              sendCommonDetailClickEvent(
                getTrackingPayload(quoteAccordionOpen, eventAction.Quotes),
                {
                  eventCategory: eventCategory.QuickOrderPage,
                  eventAction: eventAction.Quotes,
                  eventLabel: userId,
                  eventInteractionType: 0,
                }
              )
              setQuoteAccordionOpen(!quoteAccordionOpen)
            }}
            label={<FormattedMessage id="QUOTES" defaultMessage="Quotes" />}
          >
            {isB2BUser && (
              <ContentRow
                firstColumn={
                  <Button className={classes.link}>
                    <Link href={aldrichMarketSelectRoute.index()} passHref>
                      <a className={classes.marketSelect}>
                        <FormattedMessage
                          id="ALDRICH_MARKET_SELECT"
                          defaultMessage="Aldrich® Market Select"
                        />
                        {' >'}
                      </a>
                    </Link>
                  </Button>
                }
              />
            )}
            <Quotes handleQuoteClick={handleQuoteClick} />
          </ContentArea>
        )
      case QuickOrderModalContentType.List:
        return (
          <ContentArea
            key={`content-area-${i}`}
            gaEvent={() => {
              sendCommonDetailClickEvent(
                getTrackingPayload(listAccordionOpen, eventAction.Lists),
                {
                  eventCategory: eventCategory.QuickOrderPage,
                  eventAction: eventAction.Lists,
                  eventLabel: userId,
                  eventInteractionType: 0,
                }
              )
              setListAccordionOpen(!listAccordionOpen)
            }}
            label={<FormattedMessage id="LISTS" defaultMessage="Lists" />}
          >
            <Lists
              lists={lists || []}
              handleListClick={handleListClick}
              noListsItems={noListsItems}
            />
          </ContentArea>
        )
      case QuickOrderModalContentType.SavedCart:
        return (
          <ContentArea
            key={`content-area-${i}`}
            gaEvent={() => {
              sendCommonDetailClickEvent(
                getTrackingPayload(savedCartAccordionOpen, 'saved cart'),
                {
                  eventCategory: eventCategory.QuickOrderPage,
                  eventAction: 'saved cart',
                  eventLabel: userId,
                  eventInteractionType: 0,
                }
              )
              setSavedCartAccordionOpen(!savedCartAccordionOpen)
            }}
            label={
              <FormattedMessage id="SAVED_CARTS" defaultMessage="Saved Carts" />
            }
          >
            <SavedCarts handleSavedCartClick={handleSavedCartClick} />
          </ContentArea>
        )
    }
  }

  return (
    <>
      {isDesktop && (
        <>
          <CommonCard edgeStyle="overlay" greyBackground>
            <Grid container item spacing={2} xs={12}>
              <Grid item xs={12}>
                <h2 className={classes.header}>
                  <FormattedMessage {...messages.ADD_TO_QUICK_ORDER} />
                </h2>
              </Grid>
              {userIsLoggedIn ? (
                <Grid container item xs={12}>
                  {listsLoading ? (
                    <LoadingSkeleton />
                  ) : (
                    contentTypes.map(renderType)
                  )}
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  className={classes.addToOrderLoginContainer}
                >
                  <Typography className={classes.addToOrderLoginDescription}>
                    <FormattedMessage
                      {...messages.ADD_TO_QUICK_ORDER_DESCRIPTION}
                    />
                  </Typography>
                  <MuiLink
                    className={classes.addToOrderLoginLink}
                    href={`${loginRoute.index()}?redirect=${encodeURIComponent(
                      router.asPath || homeRoute.index()
                    )}`}
                    onClick={() =>
                      sendCommonDetailClickEvent(
                        {
                          event: 'quick_order_widget_interaction',
                          action: 'click sign in prompt',
                          section: 'add to quick order',
                          component: 'right rail',
                          elementType: 'link',
                          elementText:
                            messages.SIGN_IN_TO_GET_STARTED.defaultMessage.toLowerCase(),
                          linkUrl: `${loginRoute.index()}?redirect=${encodeURIComponent(
                            router.asPath || homeRoute.index()
                          )}`,
                          coreEvent: 'no',
                        },
                        {
                          eventCategory: eventCategory.QuickOrderPage,
                          eventAction:
                            eventAction.LogInToViewHistoryAndFavorites,
                          eventLabel: cookies._ga || '',
                          eventInteractionType: 0,
                        }
                      )
                    }
                  >
                    <FormattedMessage {...messages.SIGN_IN_TO_GET_STARTED} />
                  </MuiLink>
                </Grid>
              )}
            </Grid>
          </CommonCard>
          <AddToOrderModal
            contentType={modalContentType}
            onClose={() => setIsModalOpen(!isModalOpen)}
            open={isModalOpen}
            order={currentOrder}
            quote={currentQuote}
            savedCart={currentSavedCart}
            list={currentList}
          />
        </>
      )}
    </>
  )
}

export default AddToOrder
